import { Prompt } from "./types";

/**
 * Maps GEO output type to Schema.org type
 */
const getOutputTypeSchema = (outputType?: string) => {
  switch (outputType) {
    case 'image':
      return { "@type": "ImageObject" };
    case 'code':
      return { "@type": "SoftwareSourceCode" };
    case 'audio':
      return { "@type": "AudioObject" };
    case 'structured':
      return { "@type": "DataFeed" };
    default:
      return { "@type": "Text" };
  }
};

/**
 * Maps difficulty to Schema.org educationalLevel
 */
const getEducationalLevel = (difficulty?: string) => {
  switch (difficulty) {
    case 'beginner':
      return 'Beginner';
    case 'intermediate':
      return 'Intermediate';
    case 'advanced':
      return 'Advanced';
    default:
      return undefined;
  }
};

/**
 * Creates a Schema.org "HowTo" structured data for prompts
 * Enhanced with GEO fields for AI search engine discoverability
 */
export const createPromptSchema = (prompt: Prompt, baseUrl: string) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": prompt.title || `AI Prompt for ${prompt.geoMetadata?.use_case || 'general use'}`,
  "description": prompt.description || prompt.content.slice(0, 160),
  "step": [{
    "@type": "HowToStep",
    "name": "Copy and use the prompt",
    "text": prompt.content
  }],
  "tool": prompt.geoMetadata?.models?.recommended ? [{
    "@type": "HowToTool",
    "name": prompt.geoMetadata.models.recommended
  }] : undefined,
  "keywords": [
    prompt.geoMetadata?.intent,
    prompt.geoMetadata?.use_case,
    prompt.category,
    ...(prompt.geoMetadata?.keywords || [])
  ].filter(Boolean).join(", "),
  "educationalLevel": getEducationalLevel(prompt.geoMetadata?.difficulty),
  "author": {
    "@type": "Person",
    "name": prompt.username,
    "url": `${baseUrl}/users/${prompt.userId}`
  },
  "datePublished": prompt.createdAt,
  "url": `${baseUrl}/prompts/${prompt.id}`,
  "potentialAction": {
    "@type": "UseAction",
    "target": `${baseUrl}/prompts/${prompt.id}`,
    "result": getOutputTypeSchema(prompt.geoMetadata?.output_type)
  },
  "interactionStatistic": [
    { 
      "@type": "InteractionCounter", 
      "interactionType": "https://schema.org/LikeAction", 
      "userInteractionCount": prompt.likes 
    },
    { 
      "@type": "InteractionCounter", 
      "interactionType": "https://schema.org/CommentAction", 
      "userInteractionCount": prompt.comments 
    },
    { 
      "@type": "InteractionCounter", 
      "interactionType": "https://schema.org/CopyAction", 
      "userInteractionCount": prompt.copyCount || 0 
    }
  ]
});

/**
 * Creates a Schema.org "CreativeWork" structured data for broader AI indexing
 * Enhanced with GEO fields for better categorization
 */
export const createCreativeWorkSchema = (prompt: Prompt, baseUrl: string) => ({
  "@context": "https://schema.org",
  "@type": "CreativeWork",
  "name": prompt.title || `AI Prompt - ${prompt.geoMetadata?.use_case || prompt.category}`,
  "text": prompt.content,
  "description": prompt.description || prompt.content.slice(0, 160),
  "genre": prompt.geoMetadata?.intent || undefined,
  "about": prompt.geoMetadata?.use_case ? prompt.geoMetadata.use_case.replace(/-/g, ' ') : undefined,
  "applicationCategory": "AI Prompt",
  "softwareRequirements": prompt.geoMetadata?.models?.recommended,
  "keywords": [
    prompt.geoMetadata?.intent,
    prompt.geoMetadata?.use_case,
    ...(prompt.geoMetadata?.keywords || [])
  ].filter(Boolean).join(", "),
  "educationalLevel": getEducationalLevel(prompt.geoMetadata?.difficulty),
  "audience": prompt.geoMetadata?.difficulty ? {
    "@type": "Audience",
    "audienceType": prompt.geoMetadata.difficulty === 'beginner' ? 'Beginners' : 
                    prompt.geoMetadata.difficulty === 'advanced' ? 'Experts' : 'General'
  } : undefined,
  "author": {
    "@type": "Person",
    "name": prompt.username
  },
  "datePublished": prompt.createdAt,
  "url": `${baseUrl}/prompts/${prompt.id}`,
  "aggregateRating": prompt.geoMetadata?.reliability ? {
    "@type": "AggregateRating",
    "ratingValue": prompt.geoMetadata.reliability.community_verified > 0 
      ? Math.min(5, 3 + (prompt.geoMetadata.reliability.community_verified / 5)) 
      : undefined,
    "ratingCount": prompt.geoMetadata.reliability.community_verified + prompt.geoMetadata.reliability.reported_issues
  } : undefined
});
